import { type ProductionTimeItem } from '@/pages/customize/composables'
import { ImprintMethodModel, ProductSpecModel } from '@/pages/customize/model'
import {
  type DropShipModel,
  type MiniAddonGroup,
  type MiniSpecGroup,
  OrderType,
  type PriceInfo,
  type TotalFee
} from '@/pages/customize/types'
import { type AjaxResult, apiGet, apiPost, apiPostWithForm } from './helper'

export type UpdateTotalOptions = {
  pageAction: string
  isFreeGiftCustomize:boolean
  isFreeSpecSample:boolean
  orderType: OrderType
  isBlank: boolean
  isMultiSize: boolean
  computePriceDifference: boolean
  productId: number
  selectedProductSpecList: ProductSpecModel[]
  selectedSpecList: MiniSpecGroup[]
  selectedAddonList: MiniAddonGroup[]
  selectedImprintMethodList: ImprintMethodModel[]
  selectedProductionTime: ProductionTimeItem
  dropShip?: DropShipModel
  couponCode: string
  allPriceList: ProductionTimeItem[]
}

export type CheckOrderOption = Omit<
  UpdateTotalOptions,
  'selectedProductionTime'
> & {
  productionTime?: ProductionTimeItem
}

export type CheckSurpriseOptions = {
  isSurprise: number
}

export const checkOrderOption = async (
  action: string,
  options: UpdateTotalOptions
): Promise<AjaxResult<TotalFee>> => {
  const {
    orderType,
    isFreeGiftCustomize,
    isFreeSpecSample,
    couponCode,
    computePriceDifference,
    isBlank,
    isMultiSize,
    productId,
    selectedProductSpecList,
    selectedSpecList,
    selectedAddonList,
    selectedImprintMethodList,
    selectedProductionTime,
    dropShip,
    allPriceList,
    pageAction
  } = options

  let imprintMethodSysNo = 0
  if (isBlank) {
    imprintMethodSysNo = -999999
  } else if (selectedImprintMethodList.length > 0) {
    imprintMethodSysNo = selectedImprintMethodList[0].decorationMethod.SysNo
  }

 const allValidProductionTimes = allPriceList.filter(f=>f.enabled)
  const allValidPriceList :PriceInfo[] = []
  allValidProductionTimes.forEach(f=>{
    allValidPriceList.push(...f.priceList);
  });


  let currentPrice = selectedProductionTime.priceList.find(f=>allValidPriceList.some(p=>p.PriceName === f.PriceName));
  if (!currentPrice){
    currentPrice = selectedProductionTime.priceList[0];
  }

  const soInfo = {
    IsFreeGiftCustomize: isFreeGiftCustomize,
    IsFreeSpecSample: isFreeSpecSample,
    ComputePriceDifference: computePriceDifference,
    OrderType: orderType,
    IsBlank: isBlank,
    IsMultiSize: isMultiSize,
    ProductId: productId,
    CouponCode: couponCode,
    PriceName: currentPrice?.PriceName,
    DropShipValue: '',
    TransitTime: 5,
    ProductionTime: currentPrice?.ProductionTime,
    RushDay: currentPrice?.RushDay,
    ImprintMethodSysNo: imprintMethodSysNo,
    ProductionTimes: allPriceList.filter(f=>f.enabled).map((f) => {
      return {
        productionTime: f.productionTime,
        priceName: f.title,
        enabled: f.enabled,
        selected: f.selected,
        priceDifference: f.priceDifference,
        priceInfo: f.priceList.length > 0 ? f.priceList[0] : undefined
      }
    }),
    ProductSpecSOItems: selectedProductSpecList.map((item) => {
      return {
        IsMultiSize: isMultiSize,
        productSelectedQty: {
          IsMultiSize: isMultiSize,
          InputQuantity: item.getQty(),
          SizeQty: Array.isArray(item.quantity)
            ? item.quantity.map((qty) => {
                return {
                  Size: qty.size,
                  Quantity: qty.quantity,
                  Price: qty.price,
                  Stock: null
                }
              })
            : [
                {
                  Size: '',
                  Quantity: item.quantity.quantity,
                  Price: item.quantity.price,
                  Stock: null
                }
              ]
        },
        SelectedSpecList: item.productSpec.map((sp) => {
          return {
            name: sp.name,
            selectedText: sp.value.value,
            selectedValue: sp.value.value
          }
        })
      }
    }),
    ImprintSOItems: selectedImprintMethodList.map((item) => {
      return {
        SysNo: item.decorationMethod.SysNo,
        Location: item.decorationMethod.Location,
        Method: item.decorationMethod.Method,
        SelectedColors: item.decorationMethod.selectedColors,
        ArtworkOptions: item.decorationMethod.artworkType ,
        IsVirtualDesign: item.decorationMethod.isVirtualDesign,
        VirtualDesignLogo: item.decorationMethod.virtualDesignLogo,
        VirtualDesignImage: item.decorationMethod.virtualDesignImage,
        VirtualDesignArts: item.decorationMethod.virtualDesignArts,
        VirtualDesignFonts: item.decorationMethod.virtualDesignFonts,
        VirtualDesignJson: item.decorationMethod.virtualDesignJson,
        ShareLink:item.decorationMethod.shareLink,
        IsPersonalizationSelected:
          item.decorationMethod.isPersonalizationSelected,
        // 如果选择upload logo，则把附件传到后端去
        ArtworkFiles:
          item.decorationMethod.artworkType === 1
            ? item.decorationMethod.selectedArtworkFiles.map((f) => {
                return {
                  SysNo: f.BoxNetFileSysNo,
                  FileName: f.FileName,
                  Extension: f.FileExt,
                  ThumbnailUrl: f.ThumbnailUrl
                }
              })
            : [],
        CustomerArtworkNotes: item.decorationMethod.customerNotes
      }
    }),
    SpecSOItems: selectedSpecList.map((item) => {
      return { Name: item.name, SelectedValue: item.selectedItem?.value ?? '' }
    }),
    AddonSOItems: selectedAddonList.map((item) => {
      return { Name: item.name, SelectedValue: item.selectedItem?.value ?? '',isSystemAdded:item.selectedItem?.isSystemAdded }
    }),
    AllPriceList: allPriceList.filter(f=>f.enabled)
      .filter((f) => f.productionTime !== selectedProductionTime.productionTime)
      .map((f) => {
        return {
          PriceName: f.priceList[0].PriceName,
          RushDay: f.rushDay
        }
      })
  }

  // 保存Drop ship 信息  内容为x1_|_x2_|_x3    x1表示Drop Ship Type,  目前有两种（unknown, flat rate）.   X2表示how many locations.    X3表示是否 Add more items(0/1)
  soInfo.DropShipValue = ''

  if (dropShip?.needDropShip) {
    soInfo.DropShipValue =
      '1_|_' + dropShip.locationNum + '_|_' + (dropShip.needAddMore ? '1' : '0')
  } else {
  }
  const url = `/Ajax/OrderCustomize.ashx?action=${action}&pageoption=${pageAction}`

  return await apiPost<TotalFee>(url, soInfo)
}

export const checkSurprise = async (options: CheckSurpriseOptions) => {
  const url = '/Ajax/OrderCustomize.ashx?action=setSurprise'
  // ajaxPost("setSurprise", "", data, function (json, code) {

  // });
  return await apiPostWithForm<TotalFee>(url, options)
}

/**
 * 获取订单信息
 * @param options
 * @returns
 */
export async function checkOrder(options: UpdateTotalOptions): Promise<any> {
  return await checkOrderOption('submitorder', options)
}

/**
 * 获取imprint price价格
 * @param options
 * @returns
 */
export async function getImprincePrice(
  options: UpdateTotalOptions
): Promise<any> {
  return await checkOrderOption('getImprintPrice', options)
}
/**
 * 获取imprint price价格
 * @param options
 * @returns
 */
export async function getProduction(): Promise<
  AjaxResult<Record<number, number>>
> {
  const url = '/Ajax/OrderCustomize.ashx?action=production'

  // 先把add to cart按钮禁用
  return await apiGet<Record<number, number>>(url, false)
}

/**
 * 加载订单信息
 * @returns
 */
export async function loadOrderDetail(options: any): Promise<any> {
  const param = Object.assign({}, options)
  if (options.shoppingCartSysNo) {
    param.shoppingCartSysNo = options.shoppingCartSysNo
  } else if (options.func) {
    param.func = options.func
  }

  const url = '/Ajax/OrderCustomize.ashx?action=loadOrder'
  return await apiPostWithForm(url, param)
}

/**
 * 获取imprint price价格
 * @param options
 * @returns
 */
export async function getOrderTotal(): Promise<any> {
  const url = '/Ajax/OrderCustomize.ashx?action=orderTotal'
  const params = {}
  // 先把add to cart按钮禁用
  return await apiPostWithForm<TotalFee>(url, params)
}
/**
 *
 *
 * @param {any} type 1：vip free spec sample
 * @param {any} isChecked
 */
export const setFreeSpecSample = async (
  freeSpecSampleType: number,
  checkedValue: number
) => {
  const url = '/Ajax/OrderCustomize.ashx?action=IsFreeSpecSample'
  const params = {
    freeSpecSampleType: freeSpecSampleType,
    value: checkedValue
  }
  // 先把add to cart按钮禁用
  return await apiPostWithForm<TotalFee>(url, params)
}
